import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient, axiosClient, useAuth } from '../../auth-lib';
import OrgAdminLayout from '../components/OrgAdminLayout';
import NoteComposer, { emptyDraft } from '../components/notes/NoteComposer';
import NoteList from '../components/notes/NoteList';
import { Button, Card, EmptyState, Input, Skeleton, useToast } from '../../design-system';
import '../styles/ClientNotes.css';

// Notes — the firm's own writing, in one place.
//
// A note may name a client or name nothing at all. The client's Notes tab
// answers "what did we say about THIS client"; this screen answers the question
// people actually turn up with — "where was that note about the GST notice",
// where the client is the part they have forgotten, or where there was never a
// client in it. Both read the same collection through the same server filters.
//
// ⚠️ NOTHING HERE DECIDES WHAT IS VISIBLE. The server returns only what this
// caller may read — shared notes, plus their own personal ones and any they are
// tagged into, minus every client they are blocked from. This file must never
// grow a filter that looks like it is doing that job, because the next person
// to read it will believe it.

const VIEWS = [
  { value: 'all', label: 'All' },
  { value: 'shared', label: 'Shared' },
  { value: 'personal', label: 'Personal' },
  { value: 'mentions', label: 'Tagged me' },
];

// A SECOND, INDEPENDENT AXIS from the views above: whether a note names a
// client has nothing to do with who may read it, so the two are separate chip
// groups rather than one list of six mixed-up things.
const LINKS = [
  { value: '', label: 'Any' },
  { value: 'general', label: 'General' },
  { value: 'client', label: 'On a client' },
];

function OrgAdminNotes() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [notes, setNotes] = useState([]);
  const [canWrite, setCanWrite] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ view: 'all', link: '', q: '' });

  const [orgUsers, setOrgUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [draft, setDraft] = useState(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(emptyDraft);
  const [composing, setComposing] = useState(false);

  // The gate is the server's; this only avoids firing a request that will 403.
  const canRead = !!user?.permissions?.includes('client-notes.access');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // "Tagged me" is a different question from a kind filter — it spans both
      // visibilities — so it has its own endpoint rather than a fourth kind.
      const { data } = filters.view === 'mentions'
        ? await apiClient.get('/api/client-notes/mentions')
        : await apiClient.get('/api/client-notes/all', {
          params: {
            ...(filters.view === 'all' ? {} : { kind: filters.view }),
            ...(filters.link ? { link: filters.link } : {}),
            ...(filters.q.trim() ? { q: filters.q.trim() } : {}),
          },
        });
      setNotes(data.notes || []);
      setTruncated(!!data.truncated);
      // `mentions` doesn't answer canWrite; it is a view of the same notes, so
      // don't let switching to it take the composer away.
      if (data.canWrite !== undefined) setCanWrite(!!data.canWrite);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to load notes');
    } finally {
      setLoading(false);
    }
    // `toast` is stable from the provider; listing it re-runs the fetch on
    // every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.view, filters.link, filters.q]);

  useEffect(() => {
    if (!canRead) { setLoading(false); return undefined; }
    // Debounced so typing in the search box is one request, not one per key.
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load, canRead]);

  // The pickers. Clients are optional here — a note without one is a general
  // note — so a failed fetch leaves the picker empty rather than the page
  // broken.
  useEffect(() => {
    if (!canRead) return;
    axiosClient.get('/users').then((r) => setOrgUsers(r.data || [])).catch(() => setOrgUsers([]));
    apiClient.get('/api/clients').then((r) => setClients(r.data || [])).catch(() => setClients([]));
  }, [canRead]);

  const submit = async () => {
    if (!draft.body.trim()) return;
    setSaving(true);
    try {
      await apiClient.post('/api/client-notes', draft);
      setDraft(emptyDraft);
      setComposing(false);
      toast.success('Note added');
      load();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not save the note');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (note) => {
    setEditingId(note.id);
    setEditDraft({
      body: note.body,
      visibility: note.visibility,
      taggedUserIds: note.tagged.map((t) => t.id),
      isPinned: note.isPinned,
      // '' where the note names no client, so the picker lands on "General"
      // rather than on whichever client happens to be first in the list.
      clientId: note.clientId || '',
    });
  };

  const saveEdit = async (id) => {
    setSaving(true);
    try {
      await apiClient.patch(`/api/client-notes/${id}`, editDraft);
      setEditingId(null);
      toast.success('Note updated');
      load();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not update the note');
    } finally {
      setSaving(false);
    }
  };

  const togglePin = async (note) => {
    try {
      await apiClient.patch(`/api/client-notes/${note.id}`, { isPinned: !note.isPinned });
      load();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not pin the note');
    }
  };

  const remove = async (note) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete this note? It stays in the audit trail but disappears from here.')) return;
    try {
      await apiClient.delete(`/api/client-notes/${note.id}`);
      toast.success('Note deleted');
      load();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not delete the note');
    }
  };

  if (!canRead) {
    return (
      <OrgAdminLayout title="Notes" subtitle="The firm's own notes — never visible to a client">
        <EmptyState
          icon="🔒"
          title="You don't have access to notes"
          description="Notes need the client-notes.access permission. An admin can grant it from Permissions."
        />
      </OrgAdminLayout>
    );
  }

  return (
    <OrgAdminLayout
      title="Notes"
      subtitle="Your own notes and the firm's — internal only, never visible to a client"
    >
      <div className="client-notes-page">
        {canWrite && (
          <Card className="cn-composer-card">
            {composing ? (
              <NoteComposer
                value={draft}
                onChange={setDraft}
                onSubmit={submit}
                onCancel={() => { setComposing(false); setDraft(emptyDraft); }}
                submitLabel="Add note"
                saving={saving}
                orgUsers={orgUsers}
                clients={clients}
                placeholder="Anything worth writing down — with or without a client attached."
              />
            ) : (
              <button type="button" className="cn-compose-open" onClick={() => setComposing(true)}>
                Write a note…
              </button>
            )}
          </Card>
        )}

        <Card className="cn-toolbar-card">
          <div className="cn-toolbar">
            <div className="cn-view-chips">
              {VIEWS.map((v) => (
                <Button
                  key={v.value}
                  size="sm"
                  variant={filters.view === v.value ? 'primary' : 'secondary'}
                  onClick={() => setFilters((f) => ({ ...f, view: v.value }))}
                >
                  {v.label}
                </Button>
              ))}
            </div>
            <div className="cn-view-chips cn-link-chips" role="group" aria-label="Attached to a client">
              {LINKS.map((l) => (
                <button
                  type="button"
                  key={l.value || 'any'}
                  className={`cn-kind ${filters.link === l.value ? 'is-on' : ''}`}
                  aria-pressed={filters.link === l.value}
                  // The endpoint behind "Tagged me" is a fixed list with no
                  // link filter, so the chips would promise a narrowing they
                  // cannot do.
                  disabled={filters.view === 'mentions'}
                  onClick={() => setFilters((f) => ({ ...f, link: l.value }))}
                >
                  {l.label}
                </button>
              ))}
            </div>
            <form className="cn-search" onSubmit={(e) => { e.preventDefault(); load(); }}>
              <Input
                aria-label="Search notes"
                placeholder="Search note text…"
                value={filters.q}
                onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                // Search runs server-side against the whole firm; "Tagged me"
                // is a fixed list, so the box would promise a filter it has no
                // endpoint for.
                disabled={filters.view === 'mentions'}
              />
            </form>
          </div>
        </Card>

        {loading ? (
          <Card><Skeleton.Text lines={6} /></Card>
        ) : notes.length === 0 ? (
          <EmptyState
            icon="🗒️"
            title={filters.q ? 'No notes match that search' : 'No notes yet'}
            description={filters.view === 'mentions'
              ? 'Notes that tag you will appear here.'
              : 'Write one above. A note can stand on its own, or name the client it is about.'}
          />
        ) : (
          <Card>
            <NoteList
              notes={notes}
              showClient
              onOpenClient={(c) => navigate(`/org-admin/clients/${c.id}`)}
              editingId={editingId}
              editDraft={editDraft}
              setEditDraft={setEditDraft}
              onStartEdit={startEdit}
              onCancelEdit={() => setEditingId(null)}
              onSaveEdit={saveEdit}
              onTogglePin={togglePin}
              onDelete={remove}
              saving={saving}
              orgUsers={orgUsers}
              clients={clients}
            />
          </Card>
        )}

        {truncated && (
          // Said out loud rather than presenting a capped list as the whole of
          // the firm's notes.
          <p className="cn-truncated">
            Showing the most recent {notes.length}. Narrow it with the search box.
          </p>
        )}
      </div>
    </OrgAdminLayout>
  );
}

export default OrgAdminNotes;
